import { useState, useEffect, useCallback } from "react";
import { t } from "@/lib/i18n";
import { CalendarBlank, Trash, Info, GoogleLogo, ArrowSquareOut } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { PageShell } from "@/components/PageShell";
import { MailboxSwitcher, useMailboxApi } from "@/components/MailboxSwitcher";
import api, { errDetail } from "@/lib/api";

export default function CalendarPage() {
  const { mailbox, setMailbox, mbApi } = useMailboxApi();
  const [events, setEvents] = useState([]);
  const [gmailConn, setGmailConn] = useState(null);
  const load = useCallback(() => mbApi.get("/calendar/events").then((r) => setEvents(r.data)), [mbApi]);
  useEffect(() => {
    load();
    mbApi.get("/connections").then((r) => {
      setGmailConn((r.data || []).find((c) => c.provider === "gmail" && c.status === "active") || null);
    }).catch(() => {});
  }, [load]);

  const remove = async (ev) => {
    try { await mbApi.delete(`/calendar/events/${ev.id}`); toast.success(t("cal.removed", "Event removed")); load(); } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <PageShell title={t("cal.title", "Calendar")} subtitle={t("cal.subtitle", "Meetings scheduled by talking to your Email Agent.")} testId="calendar-page">
      <MailboxSwitcher mailbox={mailbox} onChange={setMailbox} />
      {gmailConn ? (
        <div data-testid="calendar-google-banner" className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-md p-3 mb-6 text-sm text-emerald-900">
          <GoogleLogo size={18} className="mt-0.5 shrink-0" weight="bold" />
          <span>{t("cal.synced", "Synced with Google Calendar ({email}). Meetings the agent schedules are created directly in your Google Calendar.", { email: gmailConn.email })}</span>
        </div>
      ) : (
        <div data-testid="calendar-local-banner" className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-3 mb-6 text-sm text-amber-900">
          <Info size={18} className="mt-0.5 shrink-0" />
          <span>{t("cal.local_banner", "Connect your Google account in Settings to sync meetings with Google Calendar. Until then, events are stored locally.")}</span>
        </div>
      )}
      <div className="space-y-2">
        {events.map((ev) => (
          <div key={ev.id} data-testid={`event-card-${ev.id}`} className="border border-gray-200 rounded-md p-4 flex items-center gap-3">
            <div className="w-12 text-center border border-gray-200 rounded-md py-1 shrink-0">
              <div className="text-[10px] uppercase text-red-500 font-bold">{dayjs(ev.start).format("MMM")}</div>
              <div className="font-heading font-bold text-lg text-gray-900 leading-none">{dayjs(ev.start).format("D")}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">{ev.title}</div>
              <div className="text-xs text-gray-500">
                {dayjs(ev.start).format("dddd, h:mm A")} · {ev.duration_minutes} min{ev.with_email ? ` · ${t("cal.with", "with")} ${ev.with_email}` : ""}
              </div>
            </div>
            {ev.html_link && (
              <a href={ev.html_link} target="_blank" rel="noreferrer" data-testid={`event-link-${ev.id}`}
                 className="flex items-center gap-1 text-xs text-blue-600 hover:underline font-medium shrink-0">
                <ArrowSquareOut size={13} /> {t("cal.open", "Open")}
              </a>
            )}
            <span className={`text-[10px] uppercase font-semibold rounded px-2 py-0.5 ${ev.provider === "google" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{ev.provider}</span>
            <button data-testid={`event-delete-${ev.id}`} onClick={() => remove(ev)} className="text-gray-400 hover:text-red-600 transition-colors p-1"><Trash size={15} /></button>
          </div>
        ))}
        {events.length === 0 && (
          <div className="border border-dashed border-gray-300 rounded-md p-10 text-center text-sm text-gray-400">
            <CalendarBlank size={28} className="mx-auto mb-2 opacity-50" />
            {t("cal.none", "No meetings yet — ask the agent: \"Schedule a meeting with Elena tomorrow at 2pm\"")}
          </div>
        )}
      </div>
    </PageShell>
  );
}
